import { debounce } from 'debounce';
import WaveSurfer from 'wavesurfer.js';

import { MIN_CELL_DUR } from 'constants/params';
import { StoryConfig } from 'services/story.service';
import { StoryBoard } from 'services/storyboard.service';
import { getSbTracks, isNull } from 'components/app/_helpers/_app.helper';
import { convertTransition } from 'components/app/_helpers/_app.helper';
import { getMarkerNodes } from 'components/app/audio-track/_mediator/audio-track.helper';
import { EMPTY_CELL } from 'components/app/editor/_mediator/editor.slice';

import audioTrackStyles from 'components/app/audio-track/_audio-track.module.scss';
import trackClipStyles from 'components/app/app-components/track-clip/_track-clip.module.scss';

type MarkerType = 'start' | 'end';

const getAppropriateRegion = (payload: {
  markerType: MarkerType;
  config: StoryConfig;
  regionsTimecodes: number[];
  currentTime: number;
}) => {
  const { markerType, regionsTimecodes, config, currentTime } = payload;

  return markerType === 'start'
    ? regionsTimecodes.find((region) => currentTime <= region + convertTransition(config.transitionTime))
    : regionsTimecodes.reduce((prev, curr) =>
        Math.abs(curr - currentTime) <= Math.abs(prev - currentTime) ? curr : prev
      );
};

const getAppropriateRegionIndex = (regionsTimecodes: number[], region?: number) => {
  return region ? regionsTimecodes.indexOf(region) : undefined;
};

export const getMarkerPositionIdentifiers = (payload: {
  config: StoryConfig;
  regionsTimecodes: number[];
  fraction: Fraction;
  markerType: MarkerType;
  duration: Duration;
}) => {
  const { config, regionsTimecodes, fraction, markerType, duration } = payload;

  const currentTime = fraction * duration;
  const appropriateRegion = getAppropriateRegion({ markerType, regionsTimecodes, config, currentTime });
  const appropriateRegionIndex = getAppropriateRegionIndex(regionsTimecodes, appropriateRegion);
  const closeToStartEdge = currentTime <= MIN_CELL_DUR;
  const closeToEndEdge = currentTime > duration - MIN_CELL_DUR;
  const startRangeMarkerUnchanged =
    appropriateRegionIndex === config.startMarkerId && markerType === 'start' && !closeToStartEdge;
  const endRangeMarkerUnchanged =
    appropriateRegionIndex === config.finishMarkerId && markerType === 'end' && !closeToEndEdge;
  const startAfterEnd =
    appropriateRegionIndex &&
    config.finishMarkerId &&
    appropriateRegionIndex >= config.finishMarkerId &&
    markerType === 'start';
  const endBeforeStart =
    appropriateRegionIndex !== undefined &&
    !isNull(config.startMarkerId) &&
    appropriateRegionIndex <= config.startMarkerId &&
    markerType === 'end';

  const isPositionInvalid =
    !appropriateRegion || startRangeMarkerUnchanged || endRangeMarkerUnchanged || startAfterEnd || endBeforeStart;

  return {
    markerType,
    isPositionInvalid,
    closeToEndEdge,
    closeToStartEdge,
    appropriateRegion,
    appropriateRegionIndex,
  };
};

export const getRangeMarkers = (start: number, end: number, draggable: boolean) => [
  {
    time: start,
    label: 'Start',
    color: '#87c9b8',
    position: 'top',
    draggable,
  },
  {
    time: end,
    label: 'End',
    color: '#f35a4f',
    position: 'bottom',
    draggable,
  },
];

export const setRangeRegionsClass = (range: number[], ws: WaveSurfer) => {
  debounce(
    () => {
      const markerNodes = getMarkerNodes(ws);

      markerNodes.forEach((el, index) => {
        const currentClassList = (el as HTMLElement)?.classList;
        currentClassList?.remove(audioTrackStyles[`region--out-of-range`]);
        if (!range.includes(index)) currentClassList?.add(audioTrackStyles[`region--out-of-range`]);
      });
    },
    'setRangeRegionsClass',
    10
  );
};

export const setRangeClipsClass = (payload: {
  storyBoard: StoryBoard;
  markers: number[];
  markerType: MarkerType;
  closeToEndEdge: boolean;
  closeToStartEdge: boolean;
  start?: number | null;
  end?: number | null;
}) => {
  const { storyBoard, markers, markerType, start, end, closeToEndEdge, closeToStartEdge } = payload;

  getSbTracks().forEach((track) => {
    storyBoard[track].forEach((cell, index) => {
      const clipInRange = index > (start ?? -1) && index <= (end ?? markers.length);

      if (cell === EMPTY_CELL) return;

      const currentClassList = document.querySelector(`.${track}_${index}`)?.classList;

      currentClassList?.remove(trackClipStyles['ClipContainer--outOfRange']);

      if (
        clipInRange ||
        (markerType === 'start' && closeToStartEdge && index === 0) ||
        (markerType === 'end' && closeToEndEdge && index === storyBoard[track].length - 1)
      )
        return;

      currentClassList?.add(trackClipStyles['ClipContainer--outOfRange']);
    });
  });
};

export const getMarkersEnabledRange = (
  storyConfig: StoryConfig | null,
  markersLength: number,
  type: 'clip' | 'marker'
) => {
  if (!storyConfig || !markersLength) return [];

  const isClip = type === 'clip';
  const start = isNull(storyConfig.startMarkerId)
    ? 0
    : isClip
    ? storyConfig.startMarkerId + 1
    : storyConfig.startMarkerId;
  const end = isNull(storyConfig.finishMarkerId) ? markersLength : storyConfig.finishMarkerId;
  const range = [];

  for (let i = start; isClip ? i <= end : i < end; i++) {
    range.push(i);
  }

  return range;
};

export const getRangeMarkerTimes = (payload: {
  finishMarkerId: number | null;
  startMarkerId: number | null;
  type?: SeekToType | 'ms';
  markers: number[];
  duration: number;
}) => {
  const { finishMarkerId, startMarkerId, duration, markers, type = 'seconds' } = payload;
  const startTime = isNull(startMarkerId) ? 0 : markers[startMarkerId];
  const endTime = isNull(finishMarkerId) || finishMarkerId! > markers.length - 1 ? duration : markers[finishMarkerId];
  switch (type) {
    case 'ms':
      return { startTime: startTime * 1000, endTime: endTime * 1000 };
    case 'fraction':
      return { startTime: startTime / duration, endTime: endTime / duration };
    default:
      return { startTime, endTime };
  }
};
